import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Card, CardBody, Drawer, DrawerBody, DrawerContent, DrawerHeader, Heading, IconButton, Input, Text, Tooltip } from "@chakra-ui/react";
import Button from "../../../components/Button";
import { post } from "../../../api/api";
import { DstSchema, PipelineImportRow, SrcSchema } from "../../../api/types";
import { typedSxMap } from "../../../utils/typedSxMap";
import "./../style/TableStyle.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FaCircleCheck, FaRegCircle } from "react-icons/fa6";
import { MdError, MdInfoOutline, MdOutlinePlaylistRemove } from "react-icons/md";

export const DRAWER_CLASS_TOP_REFERENCE = "pipeline-importer-header-divider";

type ReviewRowDetailProps = {
  isOpen: boolean;
  onClose?: () => void;
  containerRef?: any;
  importId?: string;
  rows: PipelineImportRow[] | null;
  columns: { key: string; value: string }[];
  isReadOnly?: boolean;
  dstSchema?: DstSchema;
  srcSchema?: SrcSchema;
  onUpdateRow?: (row: PipelineImportRow) => void;
  onBulkEdit?: () => void;
  importStatus?: string;
};

const styles = typedSxMap({
  container: {
    backgroundColor: "var(--color-background-menu)",
    "& .chakra-modal__close-btn": {
      display: "none",
    },
  },
  cardHeader: {
    padding: "var(--m)",
    margin: 0,
  },
  cardTitle: {
    fontSize: "24px",
    padding: 0,
    margin: 0,
  },
  cardHeading: {
    display: "flex",
    gap: "3px",
    marginBottom: "15px",
    fontSize: "var(--font-size-sm, 14px)",
    width: "100%",
    marginLeft: "45px",
    "& > *": {
      flex: 1,
    },
  },
  schemaItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "0 12px",

    "&:last-child": {
      borderBottom: "none",
    },
    "& p": {
      marginBottom: 0,
    },
  },
  cardItemOriginal: {
    display: "flex",
    flexDirection: "column",
    borderBottom: "1px solid var(--Gray-700, #344054)",
    width: "100%",
    padding: "4px 0px",
    "&:last-child": {
      borderBottom: "none",
    },
    "& p": {
      marginBottom: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  cardItemOriginalWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",

    "& p": {
      marginBottom: 0,
    },
  },
  cardItemExtracted: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: "4px 0px",

    "& p": {
      marginBottom: 0,
    },
  },
  cardItemWrapper: {
    display: "flex",
    gap: "60px",
    marginLeft: "var(--m-xs)",
    width: "calc(100% - var(--m-xs))",
  },
  label: {
    fontSize: "var(--font-size-xs, 12px)",
    fontWeight: 400,
    minWidth: "40%",
    color: "var(--color-text-soft)",
  },
  value: {
    fontSize: "var(--font-size-sm, 14px)",
    fontWeight: 400,
    minHeight: "21px",
  },
  dataInput: {
    padding: 0,
    height: "21px",
    border: "none",
    fontSize: "var(--font-size-sm, 14px)",
  },
  cardBody: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "var(--m-s) var(--m)",
  },
  card: {
    padding: "26px var(--spacing-2xl)",
    borderRadius: "var(--radius-xl, 12px)",
    border: "1px solid var(--color-border)",
    backgroundColor: "transparent",
    minHeight: "100%",
    overflow: "hidden",
  },
  drawerBody: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 var(--m) 19px var(--m)",
  },
  overlay: {
    "& .chakra-modal__content-container": {
      width: "auto",
    },
  },
  dataIcon: {
    marginRight: "4px",
    width: "20px",
  },
  closeButton: {
    backgroundColor: "var(--color-side-button-primary)",
    position: "absolute",
    right: "100%",
    borderRadius: "var(--radius-md, 8px) 0px 0px var(--radius-md, 8px)",
    maxWidth: "40px",
    height: "40px",
    minWidth: "40px",
    "&:hover": {
      backgroundColor: "var(--color-side-button-primary-hover)",
    },
    "&:focus-visible": {
      boxShadow: "none",
    },
  },
  tooltip: {
    position: "absolute",
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    maxWidth: "300px",
    zIndex: 1000,
    color: "var(--color-text)",
  },
  statusWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginBottom: "12px",
    marginLeft: "0",
    marginRight: "0",
    backgroundColor: "var(--color-background-subtle)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--color-border-light)",
    maxWidth: "100%",
  },
  statusLabel: {
    fontSize: "var(--font-size-xs)",
    fontWeight: 500,
    color: "var(--color-text-soft)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  statusMessage: {
    fontSize: "var(--font-size-sm)",
    color: "var(--color-text)",
    lineHeight: 1.5,
  },
  bottomButtons: {
    display: "flex",
    justifyContent: "space-between",
    padding: "var(--m)",
    borderTop: "1px solid var(--color-border)",
    marginTop: "auto",
  },
  errorMessage: {
    color: "var(--color-error)",
    padding: "var(--m)",
    fontSize: "var(--font-size-sm)",
    textAlign: "center",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "var(--m-xs)",
    width: "100%",
    height: "21px",
  },
  undoButton: {
    minWidth: "auto",
    height: "21px",
    padding: "0 var(--m-xs)",
    color: "var(--color-text-soft)",
    "&:hover": {
      color: "var(--color-text)",
    },
  },
});

const getDataIcon = (status: string) => {
  switch (status) {
    case "success":
      return <FaCircleCheck color="green" size={18} />;
    case "error":
      return (
        <Box
          style={{
            width: 15,
          }}>
          <MdError
            color={"var(--color-cell-error)"}
            size={21}
            style={{
              marginLeft: "-1px",
            }}
          />
        </Box>
      );

    default:
      return <FaRegCircle color="gray" size={18} />;
  }
};

export default function ReviewRowDetail({
  importId,
  isOpen,
  onClose,
  rows,
  columns,
  isReadOnly,
  dstSchema,
  srcSchema,
  onUpdateRow,
  onBulkEdit,
  importStatus,
}: ReviewRowDetailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const previousRow = useRef<number>(-1);
  const inputsRef = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const isDirtyField = useRef<{ [key: string]: boolean | null }>({});
  const drawerRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [bulkEditError, setBulkEditError] = useState<string | null>(null);
  const [hasEdits, setHasEdits] = useState(false);

  const handleClose = () => {
    setBulkEditError(null);
    setHasEdits(false);
    onClose && onClose();
  };

  const editCell = async ({
    id,
    importId,
    rowIndex,
    cellKey,
    newValue,
  }: {
    id: string;
    importId?: string;
    rowIndex?: number;
    cellKey: string;
    newValue?: string;
  }) => {
    if (!isDirtyField.current[cellKey]) return;
    const endpoint = `pipeline-import/${importId}/cell/edit`;
    const body = {
      row_index: rowIndex,
      cell_key: cellKey,
      cell_value: newValue ?? "",
    };
    const response = await post(endpoint, body);
    isDirtyField.current = { ...isDirtyField.current, [cellKey]: false };

    if (!response.ok) return;

    const currentRow = {
      ...response.data.row,
      id,
      tableIndex: Number(response.data.row.index) + 1,
    };
    onUpdateRow && onUpdateRow(currentRow);
  };

  const handleBulkEdit = async () => {
    if (!rows || !importId) return;

    const edits = Object.entries(isDirtyField.current)
      .filter(([_, isDirty]) => isDirty)
      .map(([key]) => ({
        cell_key: key,
        cell_value: inputsRef.current[key]?.value ?? "",
      }));

    if (edits.length === 0) return;

    setIsSaving(true);
    setBulkEditError(null);

    try {
      const response = await post(`pipeline-import/${importId}/cells/edit`, {
        row_indexes: rows.map((row) => Number(row.index)),
        edits,
      });

      if (!response.ok) {
        throw new Error(response.error || "Failed to save changes");
      }

      isDirtyField.current = {};
      setHasEdits(false);
      handleClose();
      onBulkEdit && onBulkEdit();
    } catch (error) {
      setBulkEditError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTop(document.querySelector(`.${DRAWER_CLASS_TOP_REFERENCE}`)?.getBoundingClientRect().top || 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && previousRow.current !== rows?.[0]?.index) {
      isDirtyField.current = {};
      setBulkEditError(null);
      previousRow.current = rows?.[0]?.index || 0;
    }
  }, [rows]);

  useLayoutEffect(() => {
    ref.current?.parentElement?.focus();
  });

  return (
    <>
      <Box ref={ref} sx={styles.overlay}></Box>
      <Drawer
        isOpen={isOpen}
        size="lg"
        placement="right"
        onClose={handleClose}
        closeOnOverlayClick={false}
        portalProps={{ containerRef: ref }}
        blockScrollOnMount={false}
        trapFocus={true}
        returnFocusOnClose={false}>
        <DrawerContent sx={styles.container} style={{ top: top }}>
          <IconButton sx={styles.closeButton} aria-label="Close" icon={<MdOutlinePlaylistRemove />} onClick={handleClose} />
          <DrawerHeader sx={styles.cardHeader}>
            {rows && rows.length > 1 ? (
              <Heading sx={styles.cardTitle}>{rows.length} Rows Selected</Heading>
            ) : (
              <Heading sx={styles.cardTitle}>Row {rows?.[0]?.tableIndex || Number(rows?.[0]?.tableIndex) + 1 || 0}</Heading>
            )}
          </DrawerHeader>

          <DrawerBody sx={styles.drawerBody}>
            {rows && rows.length > 1 ? (
              <Card variant="outline" sx={styles.card}>
                <CardBody sx={styles.cardBody}>
                  <Box sx={styles.cardHeading}>
                    <Heading size="md">Bulk Edit</Heading>
                  </Box>
                  {dstSchema?.map(({ key, name }) => (
                    <Box key={key} sx={styles.schemaItem}>
                      <Box sx={styles.dataIcon}>{isDirtyField.current[key] ? getDataIcon("success") : getDataIcon("empty")}</Box>
                      <Box sx={styles.cardItemWrapper}>
                        <Box
                          sx={styles.cardItemExtracted}
                          style={{
                            borderBottom: name || key ? "1px solid var(--color-border)" : "none",
                          }}>
                          <Text sx={styles.label}>{name || key}</Text>
                          <Box sx={styles.inputWrapper}>
                            <Input
                              ref={(el) => (inputsRef.current[key] = el)}
                              sx={styles.dataInput}
                              autoComplete="off"
                              placeholder={isDirtyField.current[key] ? "" : "+ Edit value"}
                              disabled={isReadOnly}
                              onChange={(e: any) => {
                                isDirtyField.current[key] = true;
                                setHasEdits(!hasEdits);
                              }}
                            />
                            {isDirtyField.current[key] && (
                              <Button
                                variants={["bare", "small"]}
                                onClick={() => {
                                  isDirtyField.current[key] = false;
                                  if (inputsRef.current[key]) {
                                    inputsRef.current[key]!.value = "";
                                  }
                                  setHasEdits(!hasEdits);
                                }}>
                                Undo
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </CardBody>
                <Box sx={styles.bottomButtons}>
                  <Button variants={["secondary"]} onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button variants={["primary"]} onClick={handleBulkEdit} disabled={isSaving || !Object.values(isDirtyField.current).some(Boolean)}>
                    {isSaving ? "Please wait..." : "Save Changes"}
                  </Button>
                </Box>
                {bulkEditError && <Box sx={styles.errorMessage}>{bulkEditError}</Box>}
              </Card>
            ) : (
              <>
                {(importStatus === "importing" || importStatus === "complete") && rows?.[0]?.status?.message && (
                  <Box sx={styles.statusWrapper}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <MdInfoOutline size={16} color="var(--color-text-soft)" />
                      <Text sx={styles.statusLabel}>Status Message</Text>
                    </Box>
                    <Text sx={styles.statusMessage}>{rows[0].status.message}</Text>
                  </Box>
                )}
                <Card variant="outline" sx={styles.card}>
                  <CardBody sx={styles.cardBody} ref={drawerRef}>
                    <Box sx={styles.cardHeading}>
                      <Heading size="md">Extracted data</Heading>
                      <Heading size="md">Original data</Heading>
                    </Box>
                    {rows && rows.length > 0 && dstSchema?.[0]?.mapping_result && (
                      <>
                        {dstSchema.map(({ key, name, mapping_result }) => {
                          const value = rows[0].destination_values?.[key];
                          const sourceColumns = mapping_result?.rule?.source_columns || [];
                          const originalDataKeys = sourceColumns?.map((index: number) => {
                            if (srcSchema && srcSchema?.[index]) {
                              srcSchema[index].checked = true;
                            }
                            return srcSchema?.[index]?.name;
                          });
                          const errors = rows[0].errors?.[key];
                          return (
                            <Box sx={styles.schemaItem} key={key + rows[0].index + name + value}>
                              {errors ? (
                                <Tooltip
                                  sx={styles.tooltip}
                                  label={errors[0].message}
                                  aria-label={errors[0].message}
                                  portalProps={{ containerRef: drawerRef }}
                                  arrowShadowColor="var(--color-border)">
                                  <span>
                                    <Box sx={styles.dataIcon}>{getDataIcon("error")}</Box>
                                  </span>
                                </Tooltip>
                              ) : rows[0].destination_values[key] ? (
                                <Box sx={styles.dataIcon}>{getDataIcon("success")}</Box>
                              ) : (
                                <Box sx={styles.dataIcon}>{getDataIcon("empty")}</Box>
                              )}
                              <Box sx={styles.cardItemWrapper}>
                                <Box
                                  sx={styles.cardItemExtracted}
                                  style={{
                                    borderBottom: name || key ? "1px solid var(--color-border)" : "none",
                                  }}>
                                  <Text sx={styles.label}>{name || key}</Text>
                                  <Box sx={styles.inputWrapper}>
                                    <Input
                                      ref={(el) => (inputsRef.current[key] = el)}
                                      sx={styles.dataInput}
                                      autoComplete="off"
                                      placeholder="+ Add value"
                                      defaultValue={rows[0]?.destination_values[key]}
                                      disabled={isReadOnly}
                                      onBlur={(e: any) => {
                                        if (!e.target.dataset.enterPressed) {
                                          editCell({
                                            id: rows[0].id,
                                            importId,
                                            rowIndex: Number(rows[0].index),
                                            cellKey: key,
                                            newValue: e.target?.value,
                                          });
                                        }
                                        delete e.target.dataset.enterPressed;
                                      }}
                                      onKeyDown={(e: any) => {
                                        if (e.key === "Enter") {
                                          e.target.dataset.enterPressed = true;
                                          editCell({
                                            id: rows[0].id,
                                            importId,
                                            rowIndex: Number(rows[0].index),
                                            cellKey: key,
                                            newValue: e.target?.value,
                                          });
                                        }
                                      }}
                                      onChange={(e: any) => {
                                        isDirtyField.current = { ...isDirtyField.current, [key]: true };
                                      }}
                                    />
                                  </Box>
                                </Box>
                                <Box sx={styles.cardItemOriginalWrapper} key={key + rows[0].index + value}>
                                  {originalDataKeys?.map((originalDataKey: string) => {
                                    const originalDataValue = Object.values(rows[0].source_values)[0][originalDataKey];
                                    return (
                                      <Box
                                        sx={styles.cardItemOriginal}
                                        key={`${originalDataKey} - ${originalDataValue}`}
                                        style={{
                                          borderBottom: originalDataKey || originalDataValue ? "1px solid var(--color-border)" : "none",
                                        }}>
                                        <Text sx={styles.label}>{originalDataKey}</Text>
                                        <Text sx={styles.value}>{originalDataValue}</Text>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                        {srcSchema?.map(({ name: originalDataKey, checked }, index) => {
                          const originalDataValue = Object.values(rows[0].source_values)[0][originalDataKey];
                          return (
                            !checked && (
                              <Box sx={styles.schemaItem} key={"" + originalDataKey + rows[0].id + rows[0].index + originalDataValue + "-" + index}>
                                <Box sx={styles.dataIcon}></Box>
                                <Box sx={styles.cardItemWrapper} style={{ maxWidth: "100%", overflow: "hidden" }}>
                                  <Box sx={styles.cardItemExtracted} style={{ width: "50%", flexShrink: 0 }}></Box>
                                  <Box
                                    sx={styles.cardItemOriginalWrapper}
                                    style={{
                                      width: "50%",
                                      flexShrink: 0,
                                      minWidth: 0,
                                      overflow: "hidden",
                                      borderBottom: originalDataKey || originalDataValue ? "1px solid var(--color-border)" : "none",
                                    }}>
                                    <Box sx={styles.cardItemOriginal} style={{ width: "100%" }}>
                                      <Text sx={styles.label}>{originalDataKey}</Text>
                                      <Text sx={styles.value} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {originalDataValue}
                                      </Text>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            )
                          );
                        })}
                      </>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
